'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatPrice, formatPercent } from '@/lib/format';
import type { MarketCoin } from '@glintfans/shared';
import { useAppStore } from '@/store/app';
import CoinLogo from './CoinLogo';
import { useWatchlistMembership } from '@/lib/useWatchlistMembership';

interface SearchResult {
  coins: MarketCoin[];
  pairs: Array<{ symbol: string; baseAsset: string; quoteAsset?: string }>;
}

export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const { language } = useAppStore();
  const { isStarred, toggle, loading: wlLoading } = useWatchlistMembership();

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', q],
    queryFn: () => api.get<SearchResult>(`/market/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 1,
    staleTime: 2_000,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = data?.coins ?? [];

  return (
    <div ref={boxRef} className="relative hidden flex-1 max-w-md md:block">
      <div className="flex items-center gap-2 rounded-md border border-gf-border bg-gf-surface2 px-3 py-1.5">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gf-muted">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={language !== 'en' ? '搜索币种 / 交易对...' : 'Search coins / symbols...'}
          className="w-full bg-transparent text-sm outline-none placeholder:text-gf-muted"
        />
        {isFetching && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-gf-accent" />}
      </div>

      {open && q.trim() && (
        <div className="absolute right-0 top-full z-50 mt-1 max-h-[600px] w-[420px] max-w-[85vw] overflow-auto rounded-md border border-gf-border bg-gf-surface shadow-xl">
          {results.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-gf-muted">{language !== 'en' ? `没有找到「${q}」的结果` : `No results for "${q}"`}</div>
          ) : (
            results.map((c) => {
              const pct = c.priceChangePercent24h ?? 0;
              const starred = isStarred(c.symbol);
              const isSpot = c.type === 'spot';
              return (
                <div
                  key={`${c.exchange}-${c.symbol}-${isSpot ? 's' : 'f'}`}
                  className="flex items-center gap-2 border-b border-gf-border px-3 py-2 hover:bg-gf-surface2"
                >
                  <Link
                    href={`/coin/${c.symbol}${isSpot ? '?type=spot' : ''}`}
                    onClick={() => { setOpen(false); setQ(''); }}
                    className="flex min-w-0 flex-1 items-center gap-2"
                  >
                    <CoinLogo symbol={c.symbol} size={28} />
                    <div className="leading-tight">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{c.baseAsset}</span>
                        <span className="text-xs text-gf-muted">/ {c.quoteAsset}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-gf-muted">
                        <span className={`rounded px-1 py-px ${isSpot ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                          {isSpot
                            ? (language !== 'en' ? '现货' : 'Spot')
                            : (language !== 'en' ? '永续合约' : 'Perp')}
                        </span>
                        <span>{c.exchange || 'Binance'}</span>
                      </div>
                    </div>
                    <span className="ml-auto flex flex-col items-end">
                      <span className="font-mono text-xs">{formatPrice(c.price)}</span>
                      <span className={`font-mono text-xs ${pct >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                        {formatPercent(pct)}
                      </span>
                    </span>
                  </Link>
                  {/* AUD21: star toggle shares the same membership as watchlist / detail */}
                  <button
                    type="button"
                    disabled={wlLoading}
                    aria-label={starred ? (language !== 'en' ? '从自选移除' : 'Remove from watchlist') : (language !== 'en' ? '加入自选' : 'Add to watchlist')}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle.mutate(c.symbol); }}
                    className={`shrink-0 rounded-md p-1.5 text-sm transition-colors ${starred ? 'text-gf-accent' : 'text-gf-muted hover:text-gf-text'}`}
                  >
                    {starred ? '★' : '☆'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
